import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Flex,
  Button,
  Textarea,
  Group,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';

import { buttonTextColor } from 'utils/buttonUtils';

interface InternalCommentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: (comment: string) => void;
  existingComment?: string;
}

const MAX = 500;

export const InternalCommentModal: React.FC<InternalCommentModalProps> = ({
  open,
  setOpen,
  onConfirm,
  existingComment,
}) => {
  const [comment, setComment] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const theme = useMantineTheme();
  const mode = useComputedColorScheme();

  const remaining = MAX - comment.length;
  const overLimit = remaining < 0;
  const canSubmit = comment.trim().length > 0 && !overLimit && !loading;

  // When modal opens, sync default value and focus textarea
  useEffect(() => {
    if (open) {
      setComment('');
      // slight delay so focus lands after mount/animation
      const t = setTimeout(() => textareaRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [open, existingComment]);

  const handleClose = () => setOpen(false);

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      await Promise.resolve(onConfirm(comment.trim()));
      setOpen(false);
      setComment('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      closeOnEscape
      withCloseButton
      centered
      size="lg"
      title={<Text fw={700}>Add Internal Comment</Text>}
      radius="lg"
      shadow="xl"
      overlayProps={{ blur: 3, opacity: 0.5 }}
      styles={{
        content: {
          background:
            mode === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
          border:
            mode === 'dark'
              ? `1px solid ${theme.colors.dark[4]}`
              : `1px solid ${theme.colors.gray[3]}`,
        },
        header: { paddingBottom: 0 },
        title: {
          color: mode === 'dark' ? theme.white : undefined,
        },
        close: {
          border: `1px solid ${
            mode === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]
          }`,
          background: mode === 'dark' ? theme.colors.dark[6] : theme.white,
          borderRadius: 10,
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${
              mode === 'dark' ? theme.colors.blue[6] : theme.colors.blue[5]
            }`,
          },
        },
      }}
    >
      <Stack gap="md">
        <Stack gap={6}>
          <Text size="sm" c="dimmed">
            Internal Comment
          </Text>

          <Textarea
            ref={textareaRef}
            autosize
            minRows={5}
            data-testid="internal-comment-textarea"
            placeholder="Enter your internal comment here..."
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
            }}
            styles={(theme) => ({
              input: {
                background: mode === 'dark' ? theme.colors.dark[8] : undefined,
                borderColor: overLimit
                  ? theme.colors.red[6]
                  : mode === 'dark'
                  ? theme.colors.dark[4]
                  : theme.colors.gray[4],
              },
            })}
          />

          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Shift+Enter = newline • Cmd/Ctrl+Enter = confirm
            </Text>
            <Text size="xs" c={overLimit ? 'red' : 'dimmed'}>
              {remaining} characters left
            </Text>
          </Group>

          {overLimit && (
            <Text size="xs" c="red">
              Comment exceeds the {MAX} character limit.
            </Text>
          )}
        </Stack>

        <Flex justify="end" gap="md" pt="sm">
          <Button
            color="gray"
            c={buttonTextColor(mode, false)}
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="green"
            c={buttonTextColor(mode, false)}
            loading={loading}
            disabled={!canSubmit}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
};
